import { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { alpha } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import api from 'services/api'
import { Stamp } from 'components/core/Clock'
import SplitPanes, {
  opensDetail,
  useDesktop,
  useOpenItem,
} from 'components/core/SplitPanes'
import {
  CostTable,
  MomentTiming,
  SpanTiming,
  momentTimingLine,
  spanCostLine,
} from './SpanCost'
import {
  QuoteButton,
  QuoteLine,
  MomentChips,
  MomentMeta,
  ProfileLink,
  WholeMoment,
  useSpanLedger,
  SpanLedger,
  SpanTally,
  RouterHeardButton,
} from './MomentCard'
import WholeSpan, { spanWordsLine, useMomentSpan } from './WholeSpan'
import { TAKEN_BACK } from '../Reports/ReportCard'

// The moments page in two panes (2026-10-20; it was one column of cards that
// each opened in place). The list is on the left, a row per moment: its line,
// hook and chips, and the play button that hears the quote as the phone will.
// The picked moment is on the right, held in view while the list scrolls, in
// sections that each fold behind their own chevron with a summary on the
// fold: the whole moment; the conversation under it, every comment's words
// (2026-11-04); how long it took, the fold saying how long from the moment to
// its push; what the span cost; every moment the selector found in the span,
// each playable; and last, the whole span it was cut from, every word, the
// moment marked (2026-11-04). The whole moment opens with every pick on a
// desktop (owner, 2026-10-20) and starts folded on a phone, where the
// transcript would push the rest off the screen (owner, 2026-10-21); which of
// the others are open carries from one moment to the next, so stepping down
// the list (a click, or the arrow keys once it has focus) keeps the same view.
// The span's ledger is read when a moment is picked, its words when the whole
// span or its moments are open, its thread when the conversation is.

const OPEN_AT_START = {
  words: false,
  conversation: false,
  timing: false,
  cost: false,
  span: false,
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// One of the pane's folds: the chevron, the title, what it holds on one line,
// and the body only while open. The whole line toggles it; the chevron is
// its button for the keyboard. The Users page's user pane folds the same.
export const Section = ({ title, summary, open, onToggle, children }) => (
  <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
        py: 0.75,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <IconButton size="small" aria-expanded={open} aria-label={title}>
        <ChevronRightIcon
          fontSize="small"
          sx={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </IconButton>
      <Typography variant="subtitle2">{title}</Typography>
      {summary}
    </Box>
    <Collapse in={open} unmountOnExit>
      <Box sx={{ pb: 2 }}>{children}</Box>
    </Collapse>
  </Box>
)

export const Line = ({ children }) => (
  <Typography variant="caption" color="text.secondary">
    {children}
  </Typography>
)

// What stands in for the span's ledger on a fold until there is a job to
// show: why there is nothing (no span on the moment, its rows expired, no
// selection job), or a spinner while it is read.
const LedgerStatus = ({ span }) => {
  if (!span.known) return <Line>no span on the moment</Line>
  if (span.error) {
    return (
      <Line>
        {span.error === 'no such span'
          ? 'the span’s ledger is gone: no row (90 days) and no trace'
          : span.error}
      </Line>
    )
  }
  if (!span.ledger) return <CircularProgress size={12} />
  if (!span.ledger.job) return <Line>no selection job on the span</Line>
  return null
}

const turnsLine = (moment) => {
  const turns = moment.turns || []
  const speakers = new Set(turns.map((turn) => turn.speaker)).size
  return `${plural(turns.length, 'turn')} · ${plural(speakers, 'speaker')}`
}

// The quote's live thread (step 6c) on one line: its comments and how many
// people wrote them.
const conversationLine = (moment) =>
  moment.comment_count
    ? `${plural(moment.comment_count, 'comment')} · ${plural(
        (moment.commenters || []).length,
        'user'
      )}`
    : 'no comments'

// The quote's thread, read when the conversation is first opened (`seen`
// only ever turns on).
const useThread = (moment, seen) => {
  const [thread, setThread] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!seen) return undefined
    let live = true
    api.streamMetrics
      .getMomentComments({ momentId: moment.id })
      .then((data) => live && setThread(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [seen, moment.id])
  return { thread, error }
}

// The conversation under the quote (owner, 2026-11-04): every comment, oldest
// first, as staff read it, nobody's blocks or reports narrowing it. Each with
// its author's picture (their profile) and name (the page narrowed to them),
// the owner marked, when it was said on the page's clock, and its words. A
// comment taken back keeps its place, saying by whom and when: its words went
// when it was taken back. An open report on a comment is a red chip to the
// Reports page.
const Thread = ({ moment, onUser, state }) => {
  if (state.error) return <Line>{state.error}</Line>
  if (!state.thread) return <CircularProgress size={14} />
  const { comments, more } = state.thread
  if (!comments.length) return <Line>No one has commented.</Line>
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {more && <Line>The newest {comments.length}; there are older ones.</Line>}
      {comments.map((comment) => (
        <Box
          key={comment.id}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <ProfileLink
            userId={comment.author_id}
            picture={comment.picture}
            size={28}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 0.75,
              }}
            >
              <Tooltip title={`Only this account · ${comment.author_id}`}>
                <Typography
                  variant="subtitle2"
                  component="span"
                  onClick={() =>
                    onUser({
                      id: comment.author_id,
                      username: comment.username,
                    })
                  }
                  sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                >
                  {comment.username || comment.author_id.slice(-6)}
                </Typography>
              </Tooltip>
              {comment.author_id === moment.user_id && (
                <Chip
                  size="small"
                  variant="outlined"
                  label="owner"
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
              <Line>
                <Stamp ms={comment.created_at_ms} zone={comment.timezone} />
              </Line>
              {comment.open_reports > 0 && (
                <Chip
                  size="small"
                  color="error"
                  clickable
                  component={Link}
                  to="/reports"
                  label={plural(comment.open_reports, 'open report')}
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
            </Box>
            {comment.deleted_at_ms != null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                Taken back by{' '}
                {TAKEN_BACK[comment.deleted_reason] || comment.deleted_reason},{' '}
                <Stamp ms={comment.deleted_at_ms} zone={comment.timezone} />
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              >
                {comment.body}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const MomentDetail = ({ moment, player, onTrace, onUser, open, onToggle }) => {
  // The pane is keyed by the moment, so a pick reads its own span afresh and
  // opens on its whole moment, folded on a phone.
  const span = useSpanLedger(moment, true)
  const spanWords = useMomentSpan(moment, open.words || open.span)
  const thread = useThread(moment, open.conversation)
  const desktop = useDesktop()
  const [wholeOpen, setWholeOpen] = useState(desktop)
  const playing = player.current?.id === moment.id
  const ready = Boolean(span.ledger?.job)
  const rows = span.ledger?.job?.moments || []
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ mt: 0.25 }}>
          <QuoteButton moment={moment} player={player} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <QuoteLine
            moment={moment}
            active={playing && !player.paused}
            time={player.time}
          />
          <Typography variant="body2" color="text.secondary">
            {moment.quote?.hook}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <MomentMeta moment={moment} onUser={onUser} />
          </Box>
        </Box>
        <Button
          size="small"
          disabled={!moment.trace}
          onClick={() => onTrace(moment.id)}
          sx={{ textTransform: 'none' }}
        >
          Trace
        </Button>
      </Box>
      {player.error?.id === moment.id && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {player.error.message}
        </Alert>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Section
          title="Whole moment"
          summary={<Line>{turnsLine(moment)}</Line>}
          open={wholeOpen}
          onToggle={() => setWholeOpen((o) => !o)}
        >
          <WholeMoment moment={moment} player={player} />
        </Section>
        <Section
          title="Conversation"
          summary={<Line>{conversationLine(moment)}</Line>}
          open={open.conversation}
          onToggle={() => onToggle('conversation')}
        >
          <Thread moment={moment} onUser={onUser} state={thread} />
        </Section>
        <Section
          title="Timing"
          summary={<Line>{momentTimingLine(moment)}</Line>}
          open={open.timing}
          onToggle={() => onToggle('timing')}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <Typography variant="subtitle2">This moment</Typography>
              <MomentTiming moment={moment} />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <Typography variant="subtitle2">Its span</Typography>
              {ready ? (
                <SpanTiming ledger={span.ledger} moment={moment} />
              ) : (
                <LedgerStatus span={span} />
              )}
            </Box>
          </Box>
        </Section>
        <Section
          title="Cost"
          summary={
            ready ? (
              <Line>{spanCostLine(span.ledger)}</Line>
            ) : (
              <LedgerStatus span={span} />
            )
          }
          open={open.cost}
          onToggle={() => onToggle('cost')}
        >
          {ready ? (
            <CostTable ledger={span.ledger} />
          ) : (
            <LedgerStatus span={span} />
          )}
        </Section>
        <Section
          title="From this span"
          summary={
            ready ? (
              <SpanTally moment={moment} ledger={span.ledger} />
            ) : (
              <LedgerStatus span={span} />
            )
          }
          open={open.span}
          onToggle={() => onToggle('span')}
        >
          {ready ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <RouterHeardButton moment={moment} ledger={span.ledger} />
              </Box>
              <SpanLedger
                moment={moment}
                rows={rows}
                player={player}
                spanWords={spanWords}
              />
            </>
          ) : (
            <LedgerStatus span={span} />
          )}
        </Section>
        <Section
          title="Whole span"
          summary={<Line>{spanWordsLine(moment, spanWords)}</Line>}
          open={open.words}
          onToggle={() => onToggle('words')}
        >
          <WholeSpan moment={moment} player={player} state={spanWords} />
        </Section>
      </Box>
    </Paper>
  )
}

const MomentRow = ({ moment, picked, onPick, player, onUser, mediaMode }) => {
  const ref = useRef(null)
  const playing = player.current?.id === moment.id
  // The arrow keys move the pick past the edge of the view; follow it.
  useEffect(() => {
    if (picked) ref.current?.scrollIntoView({ block: 'nearest' })
  }, [picked])
  return (
    <Paper
      ref={ref}
      variant="outlined"
      onClick={onPick}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        ...(picked
          ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            }
          : { '&:hover': { backgroundColor: theme.palette.action.hover } }),
      })}
    >
      {/* Its click reaches the row too: hearing a quote picks it. */}
      <QuoteButton moment={moment} player={player} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <QuoteLine
          moment={moment}
          active={playing && !player.paused}
          time={player.time}
          variant="subtitle1"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {moment.quote?.hook}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1,
            alignItems: 'center',
          }}
        >
          <MomentChips moment={moment} mediaMode={mediaMode} />
          <MomentMeta moment={moment} onUser={onUser} />
        </Box>
      </Box>
    </Paper>
  )
}

const MomentsSplit = ({
  header,
  empty,
  rows,
  player,
  onTrace,
  onUser,
  mediaMode,
  footer,
  onEnd,
}) => {
  const [pickedId, setPickedId] = useState(null)
  const [shown, setShown] = useState(false)
  const openItem = useOpenItem()
  const [open, setOpen] = useState(OPEN_AT_START)
  // A pick the list no longer holds (a new filter or search) falls back to
  // the top row, and on a phone back to the list.
  const picked = rows.find((m) => m.id === pickedId) || rows[0] || null
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }))
  const index = picked ? rows.indexOf(picked) : -1
  const move = (delta) => {
    const next = rows[index + delta]
    if (next) setPickedId(next.id)
  }
  // A menu inside a row handles its own arrows and says so.
  const step = (event) => {
    if (event.defaultPrevented || !picked) return
    const delta = { ArrowDown: 1, ArrowUp: -1 }[event.key]
    if (!delta) return
    event.preventDefault()
    move(delta)
  }
  return (
    <SplitPanes
      header={header}
      shown={shown && rows.some((m) => m.id === pickedId)}
      onBack={() => setShown(false)}
      onStep={move}
      position={{ index, count: rows.length }}
      backLabel="Moments"
      onEnd={onEnd}
      list={
        <>
          {empty}
          <Box
            tabIndex={0}
            onKeyDown={step}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {rows.map((moment) => (
              <MomentRow
                key={moment.id}
                moment={moment}
                picked={moment.id === picked.id}
                onPick={(event) => {
                  setPickedId(moment.id)
                  if (opensDetail(event)) {
                    openItem()
                    setShown(true)
                  }
                }}
                player={player}
                onUser={onUser}
                mediaMode={mediaMode}
              />
            ))}
          </Box>
          {footer}
        </>
      }
      detail={
        picked && (
          <MomentDetail
            key={picked.id}
            moment={picked}
            player={player}
            onTrace={onTrace}
            onUser={onUser}
            open={open}
            onToggle={toggle}
          />
        )
      }
    />
  )
}

export default MomentsSplit
